﻿import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';

import { Col, InputRef, Row, Tag, TagProps, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faCheck } from '@fortawesome/pro-regular-svg-icons';

import Map from 'ol/Map';
import Control from 'ol/control/Control';
import { fromLonLat, toLonLat } from 'ol/proj';
import { Coordinate } from 'ol/coordinate';
import { MapBrowserEvent } from 'ol';

import { CoordinateNotation } from 'msg-ui-react/dist/interfaces/CoordinateNotation';
import CoordinateUtilities from 'msg-ui-react/dist/utils/CoordinateUtilities';
import CoordinateInput from 'msg-ui-react/dist/components/coordinate/CoordinateInput';

export interface Props extends Pick<TagProps, "color"> {
    map: Map | undefined,
    id?: string,
    className?: string,
    style?: CSSProperties,      
    notation?: CoordinateNotation,
    notationChanged?: (notation: CoordinateNotation) => void,
    tooltipUnits?: string;
    tooltipEdit?: string,
    tooltipChange?: string,    
    mobile?: boolean,
    onStopEdit?: (coor: Coordinate) => void
}

const DEFAULT_ZOOM = 4;
const DEFAULT_NOTATION: CoordinateNotation = 'Deg';
const DECIMALS = 0;

const MousePositionField: React.FC<Props> = ({
    map,
    id,
    className = "mouse-position-field",
    style,       
    notation = DEFAULT_NOTATION,
    notationChanged,
    color = "default",
    tooltipUnits = "Click to change notation",
    tooltipEdit = "Click to edit coordinates",
    tooltipChange = "Click to change coordinates",
    mobile,
    onStopEdit
}) => {

    const container = useRef<HTMLDivElement>(null);
    const element = useRef<HTMLDivElement>(null);  
    const input = useRef<InputRef>(null);
    const editRef = useRef<boolean>();
    const selectTimeOutRef = useRef<number>();    
    
    const [edit, setEdit] = useState<boolean>();
    const [lonLat, setLonLat] = useState<Coordinate>([0, 0]);
    const [notationInternal, setNotationInternal] = useState<CoordinateNotation>(DEFAULT_NOTATION);

   
    useEffect(() => {
        setNotationInternal(notation);
    }, [notation]);

    useEffect(() => {
        editRef.current = edit;
        window.clearTimeout(selectTimeOutRef.current);
        if (edit && input.current) {
            const e = input.current
            e.focus();            
            selectTimeOutRef.current = window.setTimeout(() => {                
                try {
                    e.setSelectionRange(0, e.input?.value?.length || 100, 'forward');
                }
                catch { }
            }, 200)                       
        }        
    }, [edit])   

    useEffect(() => {
        if (map && element.current && container.current) {

            const control = new Control({
                target: container.current,
                element: element.current
            })

            map.addControl(control);

            map.getViewport()?.addEventListener("pointerout", pointerOut);
            map.on("pointermove", pointerMove);
            updateCoordinate(map);

            return () => {         
                map.un("pointermove", pointerMove);
                map.getViewport()?.removeEventListener("pointerout", pointerOut);
                try {
                    map.removeControl(control);
                }
                catch { }
            }            
        }
    }, [map]);
   

    const pointerMove = (evt: MapBrowserEvent<UIEvent>) => updateCoordinate(evt.map, evt.coordinate);
    const pointerOut = () => updateCoordinate(map);

    const updateCoordinate = (map?: Map, coordinate?: Coordinate) => {
        // since this is not a hook, we need a reference instead of a state
        if (!editRef.current) {
            const view = map?.getView();

            let coord = coordinate ?? view?.getCenter() ?? [0, 0];
            const mapProjection = view?.getProjection();

            if (mapProjection)
                coord = toLonLat(coord, mapProjection);

            setLonLat(coord);
        }
    }

    const toggleNotation = () => {
        if (!edit) {
            const notations: CoordinateNotation[] = ['Deg', 'Min', 'Sec'];
            const index = (notations.indexOf(notationInternal) + 1) % notations.length;
            const notation = notations[index];
            if (notation) {
                setNotationInternal(notation);
                if (notationChanged)
                    notationChanged(notations[index]);
            }
        }
    }   

    const stopEdit = (value: Coordinate | undefined, changed: boolean) => {
                   
        setEdit(false);

        if (onStopEdit && value)
            onStopEdit(value);

        if (value && changed) {
            setLonLat(value);

            const view = map?.getView();
            if (view) {
                const zoom = view.getZoom() ?? DEFAULT_ZOOM;
                const projection = view?.getProjection();

                view.animate({
                    zoom: Math.max(zoom, DEFAULT_ZOOM),
                    center: projection ? fromLonLat(value, projection) : value
                })
            }
        }
        else
            setLonLat(lonLat.slice());
    }

    return (
        <Tag id={id} className={className} ref={container} color={color}
            style={{
                pointerEvents: 'auto',
                position: 'absolute',                               
                display: mobile ? 'none' : undefined,
                minWidth: 180,
                userSelect: 'none',
                textAlign: 'center',
                ...style
            }}>         

            <Row wrap={false} ref={element}>
                {
                    !edit && <>
                        <Tooltip title={tooltipEdit} placement='top'>
                            <Col flex='none' onClick={() => setEdit(true)}
                                style={{ cursor: 'pointer' }}>
                                <FontAwesomeIcon icon={faPencil} />
                            </Col>
                        </Tooltip>
                        <Tooltip title={tooltipUnits} placement='top'>
                            <Col flex='auto' onClick={toggleNotation}
                                style={{ whiteSpace: 'pre', cursor: 'pointer' }}>
                                {CoordinateUtilities.formatLonLatCoordinate(lonLat, notationInternal, DECIMALS)}
                            </Col>
                        </Tooltip>
                    </>
                }
                {
                    edit && <>
                        <Tooltip title={tooltipChange} placement='top'>
                            <Col flex='none' style={{ cursor: 'pointer' }}>
                                <FontAwesomeIcon icon={faCheck} />
                            </Col>
                        </Tooltip>
                        <Col flex='auto' onClick={toggleNotation}>
                            <CoordinateInput
                                ref={input} size='small' bordered={false}
                                value={lonLat} 
                                notation={notation} decimals={DECIMALS}
                                onBlur={stopEdit}
                                onPressEnter={stopEdit}
                                style={{
                                    lineHeight: 1,
                                    textAlign: 'center',
                                    padding: 0
                                }}
                            />
                        </Col>
                    </>
                }             
            </Row>                      
        </Tag>
    )
}

export default observer(MousePositionField);